import { Model, Factory, belongsTo } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import { randPhrase, randNumber, rand, randChanceBoolean } from '@ngneat/falso'

import type { SchemaAttributes } from '../types.js'
import type { MockServer } from './server.js'
import { createErrorResponse } from './utils.js'
import {
  getDateTimeISOFromTimestamp,
  getDateTimeTimestamp,
  getPeriod,
  getTodayDateTimeISO,
} from '@/utils/date/getter.js'
import { addToDate } from '@/utils/date/calculation.js'
import { safeMathRandom } from '@/utils/number.js'

export type BoardingDetailData = SchemaAttributes<'BoardingDetail'>

export const model = (Model as ModelDefinition<BoardingDetailData>).extend({
  client: belongsTo('client'),
  patient: belongsTo('patient'),
  complaintTypeCheckIn: belongsTo('complaintType'),
  complaintTypeCheckOut: belongsTo('complaintType'),
  patientLocation: belongsTo('patientLocation'),
})

export const factory = Factory.extend<BoardingDetailData>({
  title() {
    return randPhrase({ maxCharCount: 60 })
  },
  start() {
    const today = getTodayDateTimeISO()
    const period = getPeriod(today, 'month')
    // Choose a random date between period.from and period.to
    const fromTimestamp = getDateTimeTimestamp(period.from)
    const toTimestamp = getDateTimeTimestamp(period.to)
    const startTimestamp =
      fromTimestamp + safeMathRandom() * (toTimestamp - fromTimestamp)
    return getDateTimeISOFromTimestamp(startTimestamp)
  },
  end() {
    const duration = randNumber({ min: 3, max: 15 })
    const endDate = addToDate(this.start as string, { days: duration })
    return endDate.toISOString()
  },
  status() {
    return rand(['IN_PROGRESS', 'DISCHARGED', 'READY_FOR_DISCHARGE', null])
  },
  archived() {
    return randChanceBoolean({ chanceTrue: 0.1 })
  },
  afterCreate(boardingEvent, server) {
    if (!boardingEvent.client) {
      const client = server.create('client')
      boardingEvent.update({ client })
    }
    if (!boardingEvent.patient) {
      const patient = server.create('patient', {
        archived: false,
      })
      boardingEvent.update({ patient })
    }
    if (!boardingEvent.complaintTypeCheckIn) {
      const complaintTypeCheckIn = server.create('complaintType')
      boardingEvent.update({ complaintTypeCheckIn })
    }
    if (!boardingEvent.complaintTypeCheckOut) {
      const complaintTypeCheckOut = server.create('complaintType')
      boardingEvent.update({ complaintTypeCheckOut })
    }
    if (!boardingEvent.patientLocation) {
      const patientLocation = server.create(
        'patientLocation',
        'withAvailableForBoarding',
      )
      boardingEvent.update({ patientLocation })
    }
  },
})

export function routes(server: MockServer) {
  server.resource('boardingEvent', { path: '/boarding_events' })

  server.get('/boarding_events', function (schema) {
    return schema.where('boardingEvent', { archived: false })
  })

  server.post('/boarding_events', function (schema, request) {
    const {
      data: { attributes, relationships },
    } = JSON.parse(request.requestBody)

    const {
      client,
      patient,
      patient_location,
      complaint_type_check_in,
      complaint_type_check_out,
    } = relationships

    return schema.create('boardingEvent', {
      ...attributes,
      archived: false,
      clientId: client.data.id,
      patientId: patient.data.id,
      patientLocationId: patient_location.data.id,
      complaintTypeCheckInId: complaint_type_check_in.data.id,
      complaintTypeCheckOutId: complaint_type_check_out.data.id,
    })
  })

  server.post('/boarding_events/:id/cancel', function (schema, request) {
    const boardingEventId = request.params.id

    const boardingEvent = schema.find('boardingEvent', boardingEventId)
    boardingEvent?.update({ archived: true })
    return boardingEvent
  })

  server.get('/boarding_events/:id', function (schema, request) {
    const id = request.params.id
    const boardingEvent = schema.find('boardingEvent', id)

    if (!boardingEvent) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Boarding Event with id ${id} not found` }],
      })
    }

    return boardingEvent
  })

  server.patch('/boarding_events/:id', async function (schema, request) {
    const id = request.params.id
    const attrs = JSON.parse(request.requestBody)
    const boardingEvent = schema.find('boardingEvent', id)

    if (!boardingEvent) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Boarding Event with id ${id} not found` }],
      })
    }

    if (!attrs?.data?.attributes) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'Missing "attributes" in request body' }],
      })
    }

    await boardingEvent.update(attrs.data.attributes)
    return boardingEvent
  })
}

export function seeds(server: MockServer) {
  server.createList('boardingEvent', 10)
}
